import React, { useState, useEffect, useMemo, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Trophy, FileText, Image as ImageIcon } from "lucide-react";
import { fileToDataUrl } from "@/lib/sales";

const emptyForm = () => ({
  invoice_name: "", project_name: "", flat_no: "", address: "", city: "",
  project_amount: "", amount_received: "", sales_mom: "",
  floor_plan_pdf: "", reference_images: [], initial_quotation_pdf: "",
  client_email: "", client_phone: "",
});

export default function WonGateModal({ open, lead, onClose, onSaved }) {
  const draftKey = useMemo(
    () => (lead?.id ? `won-modal-draft:${lead.id}` : null),
    [lead?.id]
  );
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!open || !draftKey) return;
    setErr("");
    restoredRef.current = false;
    // Try restoring from localStorage first (data persists across accidental dismissals).
    let restored = null;
    try {
      const raw = window.localStorage.getItem(draftKey);
      if (raw) restored = JSON.parse(raw);
    } catch { /* storage unavailable — skip restore */ }
    const base = emptyForm();
    base.client_email = lead?.email || "";
    base.client_phone = lead?.phone || "";
    if (restored && typeof restored === "object") {
      restoredRef.current = true;
      setForm({ ...base, ...restored });
    } else {
      setForm(base);
    }
  }, [open, draftKey, lead]);

  // Persist every change to localStorage so accidental clicks-outside don't lose data.
  useEffect(() => {
    if (!open || !draftKey) return;
    try {
      window.localStorage.setItem(draftKey, JSON.stringify(form));
    } catch { /* storage unavailable — skip persist */ }
  }, [form, open, draftKey]);

  useEffect(() => {
    if (open && restoredRef.current) {
      toast.info("Draft restored", { duration: 2000 });
      restoredRef.current = false;
    }
  }, [open]);

  const clearDraft = () => {
    if (!draftKey) return;
    try { window.localStorage.removeItem(draftKey); } catch { /* ignore */ }
  };

  const onFloorPlan = async (files) => {
    if (!files?.[0]) return;
    const url = await fileToDataUrl(files[0]);
    setForm(f => ({...f, floor_plan_pdf: url}));
  };

  const onInitialQuotation = async (files) => {
    if (!files?.[0]) return;
    const url = await fileToDataUrl(files[0]);
    setForm(f => ({...f, initial_quotation_pdf: url}));
  };

  const onRefImgs = async (files) => {
    const arr = Array.from(files || []).slice(0, 6);
    const urls = await Promise.all(arr.map(fileToDataUrl));
    setForm(f => ({...f, reference_images: [...f.reference_images, ...urls].slice(0, 8)}));
  };

  const submit = async (e) => {
    e.preventDefault();
    const req = ["invoice_name","project_name","flat_no","address","city","sales_mom","project_amount","amount_received"];
    for (const k of req) if (!String(form[k] ?? "").trim()) { setErr(`"${k.replace(/_/g," ")}" is required`); return; }
    if (!form.floor_plan_pdf) { setErr("Floor plan required"); return; }
    if (!form.initial_quotation_pdf) { setErr("Initial Quotation PDF required"); return; }
    // Reference images are now OPTIONAL (Feb 2026 spec change).
    if (!form.client_email || !form.client_email.trim()) { setErr("Client email is required — used to onboard them to the portal"); return; }

    setSaving(true);
    try {
      await api.post(`/sales/leads/${lead.id}/mark-won`, {
        ...form,
        client_email: form.client_email.trim().toLowerCase(),
        client_phone: (form.client_phone || "").trim() || null,
        project_amount: Number(form.project_amount),
        amount_received: Number(form.amount_received),
      });
      toast.success(`🏆 ${lead.client_name} closed as Won · Welcome kit sent to ${form.client_email}`);
      clearDraft();
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    // Data is auto-saved to localStorage — safe to close silently. Show a soft toast
    // so the user knows their inputs are preserved.
    if (Object.values(form).some(v => Array.isArray(v) ? v.length : String(v).trim())) {
      toast.success("Draft saved · reopen anytime to continue", { duration: 2500 });
    }
    onClose();
  };

  if (!open || !lead) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={handleDismiss} data-testid="won-modal">
      <div className="w-full max-w-2xl iz-card p-8 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={handleDismiss} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500" data-testid="won-close"><X className="w-4 h-4"/></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#d4af37]/10 border border-[#d4af37] flex items-center justify-center">
            <Trophy className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Milestone Gate</p>
            <h2 className="font-display text-2xl text-white">Close as Won</h2>
            <p className="text-[11px] text-gray-500 font-mono">Your inputs auto-save — closing the modal keeps a draft you can resume later.</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Invoice Name *</label>
              <input value={form.invoice_name} onChange={(e) => setForm(f => ({...f, invoice_name: e.target.value}))} className="iz-input" required data-testid="won-invoice"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Project Name *</label>
              <input value={form.project_name} onChange={(e) => setForm(f => ({...f, project_name: e.target.value}))} className="iz-input" required data-testid="won-project"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Flat No. *</label>
              <input value={form.flat_no} onChange={(e) => setForm(f => ({...f, flat_no: e.target.value}))} className="iz-input" required data-testid="won-flat"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">City *</label>
              <input value={form.city} onChange={(e) => setForm(f => ({...f, city: e.target.value}))} className="iz-input" required data-testid="won-city"/>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Full Address *</label>
            <input value={form.address} onChange={(e) => setForm(f => ({...f, address: e.target.value}))} className="iz-input" required data-testid="won-address"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Project Amount *</label>
              <input type="number" step="0.01" value={form.project_amount} onChange={(e) => setForm(f => ({...f, project_amount: e.target.value}))} className="iz-input" required data-testid="won-project-amount"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Amount Received *</label>
              <input type="number" step="0.01" value={form.amount_received} onChange={(e) => setForm(f => ({...f, amount_received: e.target.value}))} className="iz-input" required data-testid="won-amount-received"/>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Sales MOM *</label>
            <textarea rows={3} value={form.sales_mom} onChange={(e) => setForm(f => ({...f, sales_mom: e.target.value}))} className="iz-input" required data-testid="won-mom"/>
          </div>

          {/* Client contact — used to auto-onboard the client to the portal on Won */}
          <div className="border border-[#262626] rounded-md p-4 space-y-3" data-testid="won-client-contact">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Client contact for portal invite</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Client Email *</label>
                <input
                  type="email"
                  value={form.client_email}
                  onChange={(e) => setForm(f => ({...f, client_email: e.target.value}))}
                  className="iz-input"
                  required
                  placeholder="client@example.com"
                  data-testid="won-client-email"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Client Mobile</label>
                <input
                  type="tel"
                  value={form.client_phone}
                  onChange={(e) => setForm(f => ({...f, client_phone: e.target.value}))}
                  className="iz-input"
                  placeholder="+91-90000-00000"
                  data-testid="won-client-phone"
                />
              </div>
            </div>
            <p className="text-[10px] text-gray-500 leading-relaxed">
              On save, a welcome kit is emailed to the client with portal + app download links.
              Default portal login: <span className="text-[#d4af37]">the email above</span> and password <span className="text-[#d4af37]">Client@123</span> (they will be forced to change it on first sign-in).
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Floor Plan *</label>
              <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center" data-testid="won-floor-plan-label">
                <FileText className="w-4 h-4"/> {form.floor_plan_pdf ? "Replace Floor Plan" : "Upload Floor Plan"}
                <input type="file" accept="application/pdf,image/png,image/jpeg,image/heic,image/heif,.pdf,.png,.jpg,.jpeg,.heic,.heif" hidden onChange={(e) => onFloorPlan(e.target.files)} data-testid="won-floor-plan-input"/>
              </label>
              {form.floor_plan_pdf && <p className="text-[10px] text-[#d4af37] font-mono mt-1">✓ Floor plan attached</p>}
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Reference Images <span className="text-gray-600 normal-case">(optional)</span></label>
              <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center" data-testid="won-ref-imgs-label">
                <ImageIcon className="w-4 h-4"/> Add ({form.reference_images.length})
                <input type="file" accept="image/*" multiple hidden onChange={(e) => onRefImgs(e.target.files)} data-testid="won-ref-imgs-input"/>
              </label>
            </div>
          </div>
          {form.reference_images.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {form.reference_images.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt="" className="w-full h-16 object-cover rounded border border-[#262626]"/>
                  <button type="button" onClick={() => setForm(f => ({...f, reference_images: f.reference_images.filter((_, x) => x !== i)}))} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5 text-white">
                    <X className="w-3 h-3"/>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Initial Quotation PDF *</label>
            <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center" data-testid="won-initial-quotation-label">
              <FileText className="w-4 h-4"/> {form.initial_quotation_pdf ? "Replace Quotation" : "Upload Quotation PDF"}
              <input type="file" accept="application/pdf" hidden onChange={(e) => onInitialQuotation(e.target.files)} data-testid="won-initial-quotation-input"/>
            </label>
            {form.initial_quotation_pdf && <p className="text-[10px] text-[#d4af37] font-mono mt-1">✓ Quotation attached</p>}
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={handleDismiss} className="iz-btn-secondary" data-testid="won-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="won-submit">
              {saving ? "Closing…" : "🏆 Close as Won"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
